// src/components/booking/SendRequest.jsx
import React, { useState, useCallback, useMemo, memo } from 'react';
import { 
  Send,
  Calendar,
  Users,
  MapPin,
  ArrowLeft,
  ChevronRight,
  Clock,
  DollarSign,
  MessageCircle,
  Plus,
  Minus,
  Check,
  AlertCircle,
  Loader2,
  Star,
  User,
  Mail,
  Phone,
  Globe,
  Search,
  Heart,
  Bed,
  Car,
  Building2,
  Wifi,
  Coffee,
  ParkingCircle,
  UtensilsCrossed,
  Waves,
  Dumbbell,
  Shield,
  AirVent,
  Tv,
  TreePine
} from 'lucide-react';
import { bookingService } from '../../services/bookingService';
import { firestoreService } from '../../services/firestoreService';
import { FormField, TextInput, SelectInput, TextArea, Toggle } from '../settings/shared/FormComponents';
import toast from 'react-hot-toast';
// South African Provinces and Cities
const SA_LOCATIONS = {
  'Western Cape': [
    'Cape Town', 'Stellenbosch', 'Paarl', 'George', 'Knysna', 'Hermanus', 
    'Worcester', 'Oudtshoorn', 'Mossel Bay', 'Swellendam'
  ],
  'Gauteng': [
    'Johannesburg', 'Pretoria', 'Sandton', 'Soweto', 'Roodepoort', 
    'Germiston', 'Benoni', 'Boksburg', 'Randburg', 'Kempton Park'
  ],
  'KwaZulu-Natal': [
    'Durban', 'Pietermaritzburg', 'Newcastle', 'Richards Bay', 'Ladysmith',
    'Empangeni', 'Pinetown', 'Chatsworth', 'Umlazi', 'Phoenix'
  ],
  'Eastern Cape': [
    'Port Elizabeth', 'East London', 'Uitenhage', 'Queenstown', 'King Williams Town',
    'Mthatha', 'Grahamstown', 'Port Alfred', 'Jeffreys Bay', 'Cradock'
  ],
  'Limpopo': [
    'Polokwane', 'Tzaneen', 'Thohoyandou', 'Phalaborwa', 'Mokopane',
    'Giyani', 'Musina', 'Bela-Bela', 'Makhado', 'Hoedspruit'
  ],
  'Mpumalanga': [
    'Nelspruit', 'Witbank', 'Middelburg', 'Secunda', 'Standerton',
    'Ermelo', 'Barberton', 'White River', 'Sabie', 'Hazyview'
  ],
  'North West': [
    'Rustenburg', 'Klerksdorp', 'Potchefstroom', 'Brits', 'Vryburg',
    'Lichtenburg', 'Mmabatho', 'Zeerust', 'Stilfontein', 'Hartbeespoort'
  ],
  'Free State': [
    'Bloemfontein', 'Welkom', 'Kroonstad', 'Bethlehem', 'Sasolburg',
    'Phuthaditjhaba', 'Virginia', 'Odendaalsrus', 'Parys', 'Harrismith'
  ],
  'Northern Cape': [
    'Kimberley', 'Upington', 'Kuruman', 'Springbok', 'De Aar',
    'Postmasburg', 'Calvinia', 'Prieska', 'Carnarvon', 'Vryburg'
  ]
};

// Booking Type Configurations
const BOOKING_TYPES = {
  accommodation: {
    label: 'Accommodation',
    icon: Bed,
    color: '#3b82f6',
    amenities: [
      { id: 'wifi', label: 'Free WiFi', icon: Wifi },
      { id: 'parking', label: 'Free Parking', icon: ParkingCircle },
      { id: 'breakfast', label: 'Breakfast', icon: Coffee },
      { id: 'ac', label: 'Air Conditioning', icon: AirVent },
      { id: 'tv', label: 'TV', icon: Tv },
      { id: 'kitchen', label: 'Kitchen', icon: UtensilsCrossed },
      { id: 'pool', label: 'Swimming Pool', icon: Waves },
      { id: 'gym', label: 'Fitness Center', icon: Dumbbell },
      { id: 'petfriendly', label: 'Pet Friendly', icon: TreePine },
      { id: 'security', label: '24/7 Security', icon: Shield }
    ],
    categories: [
      { value: 'hotel', label: 'Hotels' },
      { value: 'bnb', label: 'B&B' },
      { value: 'guesthouse', label: 'Guesthouses' },
      { value: 'lodge', label: 'Lodges' },
      { value: 'resort', label: 'Resorts' },
      { value: 'apartment', label: 'Apartments' },
      { value: 'villa', label: 'Villas' },
      { value: 'cottage', label: 'Cottages' },
      { value: 'farm', label: 'Farm Stays' },
      { value: 'camping', label: 'Camping' }
    ]
  },
  appointment: {
    label: 'Appointments',
    icon: Clock,
    color: '#10b981',
    services: [
      { value: 'medical', label: 'Medical' },
      { value: 'beauty', label: 'Beauty & Wellness' },
      { value: 'fitness', label: 'Fitness' },
      { value: 'consultation', label: 'Consultation' },
      { value: 'therapy', label: 'Therapy' },
      { value: 'education', label: 'Education' },
      { value: 'maintenance', label: 'Home Services' }
    ]
  },
  rental: {
    label: 'Rentals',
    icon: Car,
    color: '#f59e0b',
    categories: [
      { value: 'vehicle', label: 'Vehicle Rental' },
      { value: 'equipment', label: 'Equipment' },
      { value: 'electronics', label: 'Electronics' },
      { value: 'sports', label: 'Sports Equipment' },
      { value: 'party', label: 'Party & Events' },
      { value: 'outdoor', label: 'Outdoor Gear' },
      { value: 'musical', label: 'Musical Instruments' }
    ]
  },
  venue: {
    label: 'Venues',
    icon: Building2,
    color: '#8b5cf6',
    categories: [
      { value: 'meeting-room', label: 'Meeting Room' },
      { value: 'event-hall', label: 'Event Hall' },
      { value: 'wedding-venue', label: 'Wedding Venue' },
      { value: 'studio', label: 'Studio Space' },
      { value: 'coworking', label: 'Coworking Space' },
      { value: 'kitchen', label: 'Commercial Kitchen' },
      { value: 'warehouse', label: 'Warehouse' }
    ]
  }
};

export const SendRequest = ({ 
  bookId,
  bookingType = 'accommodation',
  bookSettings = {},
  customStyling = {},
  onRequestSent,
  onNavigateToSearch,
  availableLocations = {}
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  // Universal request data structure
  const [requestData, setRequestData] = useState({
    // Basic Info
    bookingType,
    requestType: 'flexible', // 'specific' or 'flexible'
    
    // Contact Information
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    
    // Location (flexible for accommodation)
    isLocationFlexible: false,
    preferredProvince: '',
    preferredCity: '',
    alternativeLocations: [],
    locationRadius: 50, // km for flexible searches
    
    // Dates (flexible for holiday travel)
    isDateFlexible: false,
    preferredStartDate: '',
    preferredEndDate: '',
    alternativeDates: [],
    flexibilityDays: 3, // days of flexibility
    
    // Booking-specific data
    accommodation: {
      checkIn: '',
      checkOut: '',
      adults: 2,
      children: 0,
      rooms: 1,
      accommodationType: [],
      amenities: [],
      budget: { min: 0, max: 5000, flexible: true }
    },
    
    appointment: {
      date: '',
      timeSlots: [], // preferred time slots
      duration: 60, // minutes
      serviceType: '',
      practitioner: '',
      isOnlineAvailable: false
    },
    
    rental: {
      pickupDate: '',
      returnDate: '',
      pickupLocation: '',
      returnLocation: '',
      category: '',
      insurance: false,
      deliveryRequired: false
    },
    
    venue: {
      eventDate: '',
      startTime: '',
      endTime: '',
      expectedGuests: 0,
      eventType: '',
      catering: false,
      setup: '',
      equipment: []
    },
    
    // Universal fields
    budget: { min: 0, max: 10000, currency: 'ZAR', flexible: true },
    priority: 'budget', // 'budget', 'quality', 'convenience'
    specialRequirements: '',
    additionalMessage: '',
    
    // Preferences
    instantBooking: false,
    receiveAlternatives: true,
    marketingConsent: false
  });

  const typeConfig = BOOKING_TYPES[bookingType] || BOOKING_TYPES.accommodation;
  const totalSteps = bookingType === 'accommodation' ? 4 : 3;

  // Handle form data changes
  const updateRequestData = useCallback((path, value) => {
    setRequestData(prevData => {
      const keys = path.split('.');
      const newData = { ...prevData };
      let current = newData;
      
      // Navigate to the parent of the target property
      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        } else {
          current[keys[i]] = { ...current[keys[i]] };
        }
        current = current[keys[i]];
      }
      
      // Set the final value
      current[keys[keys.length - 1]] = value;
      
      return newData;
    });
  }, []);

  const formattedLocations = useMemo(() => {
    const formatted = [];
    
    // Handle case where availableLocations is undefined or empty
    if (!availableLocations || Object.keys(availableLocations).length === 0) {
      // Fallback to SA_LOCATIONS if no availableLocations
      Object.entries(SA_LOCATIONS).forEach(([province, cities]) => {
        formatted.push({
          value: province,
          label: province,
          type: 'province'
        });
        
        cities.forEach(city => {
          formatted.push({
            value: `${city}, ${province}`,
            label: `${city}, ${province}`,
            type: 'city'
          });
        });
      });
    } else {
      // Use availableLocations when available
      Object.entries(availableLocations).forEach(([province, cities]) => {
        formatted.push({
          value: province,
          label: province,
          type: 'province'
        });
        
        if (Array.isArray(cities)) {
          cities.forEach(city => {
            formatted.push({
              value: `${city}, ${province}`,
              label: `${city}, ${province}`,
              type: 'city'
            });
          });
        }
      });
    }
    
    return formatted.sort((a, b) => {
      if (a.type !== b.type) {
        return a.type === 'province' ? -1 : 1;
      }
      return a.label.localeCompare(b.label);
    });
  }, [availableLocations]);

  // Validate current step
  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 1: // Contact & Basic Info
        if (!requestData.contactName.trim()) {
          errors.contactName = 'Name is required';
        }
        if (!requestData.contactEmail.trim()) {
          errors.contactEmail = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(requestData.contactEmail)) {
          errors.contactEmail = 'Valid email is required';
        }
        break;
        
      case 2: // Location & Dates
        if (!requestData.isLocationFlexible && !requestData.preferredProvince) {
          errors.location = 'Please select a location or enable flexible location';
        }
        if (!requestData.isDateFlexible) {
          if (bookingType === 'accommodation' && (!requestData.accommodation.checkIn || !requestData.accommodation.checkOut)) {
            errors.dates = 'Please select check-in and check-out dates or enable flexible dates';
          }
        }
        break;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle step navigation
  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  // Submit request
  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Prepare submission data with proper labeling by booking type
      const submissionData = {
        ...requestData,
        bookId: bookSettings?.bookId || bookId || 'default',
        requestType: `${bookingType}_request`, // Label by booking type
        status: 'pending',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days expiry
        source: 'booking_request_form',
        
        // Add booking-specific metadata
        metadata: {
          bookingType,
          isLocationFlexible: requestData.isLocationFlexible,
          isDateFlexible: requestData.isDateFlexible,
          totalSteps: totalSteps,
          completedAt: new Date().toISOString()
        }
      };

      // Create request in the main booking requests collection
      const requestId = await firestoreService.recordBookingRequest(bookId, submissionData);
      
      // Also create in the book's requests subcollection for book owner management
      if (bookSettings?.bookId || bookId) {
        await bookingService.createBookingRequest(
          bookSettings?.bookId || bookId, 
          {
            ...submissionData,
            requestId, // Reference to main request
            bookSpecific: true
          }
        );
      }

      // Notify relevant providers based on booking type and location
      await bookingService.notifyProvidersForRequest(submissionData);

      setSubmitStatus('success');
      
      // Notify parent component
      if (onRequestSent) {
        onRequestSent({
          ...submissionData,
          requestId
        });
      }

      // Auto-hide success message and reset form after delay
      setTimeout(() => {
        setSubmitStatus(null);
        navigateToSearch();
      }, 3000);



    } catch (error) {
      console.error('Error submitting booking request:', error);
      setSubmitStatus('error');
      // Show user-friendly error message
      const errorMessage = error.message || 'Failed to send request. Please try again.';
    } finally {
      setIsSubmitting(false);
    }
  };

  const navigateToSearch = () => {
    // Extract search criteria from the request data
    const searchCriteria = {
      location: requestData.preferredLocation || '',
      province: requestData.preferredProvince || '',
      city: requestData.preferredCity || '',
      checkIn: '',
      checkOut: '',
      adults: 2,
      children: 0,
      rooms: 1
    };

    // Extract search criteria based on booking type
    if (bookingType === 'accommodation') {
      searchCriteria.checkIn = requestData.accommodation?.checkIn || '';
      searchCriteria.checkOut = requestData.accommodation?.checkOut || '';
      searchCriteria.adults = requestData.accommodation?.adults || 2;
      searchCriteria.children = requestData.accommodation?.children || 0;
      searchCriteria.rooms = requestData.accommodation?.rooms || 1;
    }

    // Call the navigation callback with search criteria
    if (onNavigateToSearch) {
      onNavigateToSearch(searchCriteria);
    }
  };

  const renderDateFields = () => {
    switch (bookingType) {
      case 'accommodation':
        return !requestData.isDateFlexible ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Check In</label>
              <input
                type="date"
                value={requestData.accommodation.checkIn}
                onChange={(e) => updateRequestData('accommodation.checkIn', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Check Out</label>
              <input
                type="date"
                value={requestData.accommodation.checkOut}
                onChange={(e) => updateRequestData('accommodation.checkOut', e.target.value)}
                min={requestData.accommodation.checkIn || new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              Perfect for holiday travel! We'll find the best available dates and present options to you.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How flexible are you with dates?
              </label>
              <select
                value={requestData.flexibilityDays}
                onChange={(e) => updateRequestData('flexibilityDays', parseInt(e.target.value))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={1}>± 1 day</option>
                <option value={3}>± 3 days</option>
                <option value={7}>± 1 week</option>
                <option value={14}>± 2 weeks</option>
                <option value={30}>± 1 month</option>
                <option value={0}>Completely flexible</option>
              </select>
            </div>
          </div>
        );
        
      case 'appointment':
        return !requestData.isDateFlexible ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Date</label>
              <input
                type="date"
                value={requestData.appointment.date}
                onChange={(e) => updateRequestData('appointment.date', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Time</label>
              <input
                type="time"
                value={requestData.appointment.time}
                onChange={(e) => updateRequestData('appointment.time', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              We'll check availability and suggest the best appointment times for you.
            </p>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Time of Day
              </label>
              <div className="grid grid-cols-3 gap-3">
                {['Morning', 'Afternoon', 'Evening'].map(timeSlot => (
                  <button
                    key={timeSlot}
                    type="button"
                    onClick={() => {
                      const currentSlots = requestData.appointment.timeSlots || [];
                      const newSlots = currentSlots.includes(timeSlot)
                        ? currentSlots.filter(slot => slot !== timeSlot)
                        : [...currentSlots, timeSlot];
                      updateRequestData('appointment.timeSlots', newSlots);
                    }}
                    className={`p-3 border rounded-lg text-sm transition-colors ${
                      requestData.appointment.timeSlots?.includes(timeSlot)
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {timeSlot}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'rental':
        return !requestData.isDateFlexible ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Date</label>
              <input
                type="date"
                value={requestData.rental.pickupDate}
                onChange={(e) => updateRequestData('rental.pickupDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Return Date</label>
              <input
                type="date"
                value={requestData.rental.returnDate}
                onChange={(e) => updateRequestData('rental.returnDate', e.target.value)}
                min={requestData.rental.pickupDate || new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        ) : (
          <div>
            <p className="text-gray-600 mb-4">
              We'll find the best available rental periods for you.
            </p>
          </div>
        );

      case 'venue':
        return !requestData.isDateFlexible ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Event Date</label>
              <input
                type="date"
                value={requestData.venue.eventDate}
                onChange={(e) => updateRequestData('venue.eventDate', e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                <input
                  type="time"
                  value={requestData.venue.startTime}
                  onChange={(e) => updateRequestData('venue.startTime', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Time</label>
                <input
                  type="time"
                  value={requestData.venue.endTime}
                  onChange={(e) => updateRequestData('venue.endTime', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-gray-600 mb-4">
              We'll suggest the best available dates and times for your event.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <ContactInfoStep 
            requestData={requestData}
            updateRequestData={updateRequestData}
            validationErrors={validationErrors}
            typeConfig={typeConfig}
          />
        );
        
      case 2:
        return (
          <LocationDateStep 
            requestData={requestData}
            updateRequestData={updateRequestData}
            validationErrors={validationErrors}
            bookingType={bookingType}
            availableLocations={availableLocations}
            formattedLocations={formattedLocations}
            renderDateFields={renderDateFields}
          />
        );
        
      case 3:
        return bookingType === 'accommodation' ? (
          <AccommodationPreferencesStep 
            requestData={requestData}
            updateRequestData={updateRequestData}
            typeConfig={typeConfig}
          />
        ) : (
          <BookingSpecificsStep 
            requestData={requestData}
            updateRequestData={updateRequestData}
            typeConfig={typeConfig}
            bookingType={bookingType}
            renderAppointmentFields={renderAppointmentFields}
            renderRentalFields={renderRentalFields}
            renderVenueFields={renderVenueFields}
          />
        );
        
      case 4:
        return (
          <ReviewSubmitStep 
            requestData={requestData}
            updateRequestData={updateRequestData}
            typeConfig={typeConfig}
            submitStatus={submitStatus}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
          />
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12" style={customStyling}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="p-3 bg-blue-100 rounded-full inline-block mb-4">
            <typeConfig.icon className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Send {typeConfig.label} Request</h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Tell us about your requirements and we'll find the best options for you
          </p>
        </div>
        {/* Progress Indicator */}
        <StepProgress />

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-12">
          <div className="p-8 lg:p-12">
            {renderStepContent()}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between max-w-4xl mx-auto">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className="px-8 py-4 text-gray-600 hover:text-gray-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2 text-lg font-medium"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back</span>
          </button>
          
          <div className="flex items-center space-x-4">
            {currentStep < totalSteps ? (
              <button
                onClick={nextStep}
                className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 text-lg font-semibold"
              >
                <span>Next Step</span>
                <ChevronRight className="h-5 w-5" />
              </button>
            ) : null}
          </div>
        </div>

        {/* Submit Status */}
        {submitStatus && (
          <div className={`mt-8 p-6 rounded-xl flex items-center justify-center max-w-2xl mx-auto ${
            submitStatus === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {submitStatus === 'success' ? (
              <>
                <Check className="h-8 w-8 mr-4" />
                <div className="text-center">
                  <div className="text-xl font-semibold mb-1">Request sent successfully!</div>
                  <div className="text-green-600">You'll receive responses via email within 24 hours.</div>
                </div>
              </>
            ) : (
              <>
                <AlertCircle className="h-8 w-8 mr-4" />
                <div className="text-center">
                  <div className="text-xl font-semibold mb-1">Error sending request</div>
                  <div className="text-red-600">Please try again or contact support.</div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};  

  // Step Progress Indicator
  const StepProgress = ({ currentStep, totalSteps }) => (
    <div className="mb-12">
      <div className="flex items-center justify-center">
        {Array.from({ length: totalSteps }, (_, i) => i + 1).map((step) => (
          <div key={step} className="flex items-center">
            <div className={`
              flex items-center justify-center w-12 h-12 rounded-full border-2 text-sm font-medium transition-colors
              ${step <= currentStep 
                ? 'border-blue-500 bg-blue-500 text-white' 
                : 'border-gray-300 text-gray-400'
              }
            `}>
              {step < currentStep ? <Check className="h-5 w-5" /> : step}
            </div>
            {step < totalSteps && (
              <div className={`w-16 h-1 mx-4 rounded-full transition-colors ${
                step < currentStep ? 'bg-blue-500' : 'bg-gray-300'
              }`} />
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-center mt-4">
        <span className="text-sm text-gray-600">
          Step {currentStep} of {totalSteps}
        </span>
      </div>
    </div>
  );

  // Contact Information Step
  const ContactInfoStep = ({ requestData, updateRequestData, validationErrors, typeConfig }) => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <div className="p-4 rounded-full bg-blue-100 inline-block mb-4">
          <typeConfig.icon className="h-10 w-10 text-blue-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Send {typeConfig.label} Request</h2>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Tell us about your requirements and we'll find the best options for you
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField 
            label="Full Name" 
            required 
            error={validationErrors.contactName}
          >
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <TextInput
                value={requestData.contactName}
                onChange={(value) => updateRequestData('contactName', value)}
                placeholder="Enter your full name"
                className="pl-10"
                error={validationErrors.contactName}
              />
            </div>
          </FormField>

          <FormField 
            label="Email Address" 
            required 
            error={validationErrors.contactEmail}
          >
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <TextInput
                type="email"
                value={requestData.contactEmail}
                onChange={(value) => updateRequestData('contactEmail', value)}
                placeholder="Enter your email"
                className="pl-10"
                error={validationErrors.contactEmail}
              />
            </div>
          </FormField>

          <FormField 
            label="Phone Number" 
            error={validationErrors.contactPhone}
            className="md:col-span-2"
          >
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <TextInput
                type="tel"
                value={requestData.contactPhone}
                onChange={(value) => updateRequestData('contactPhone', value)}
                placeholder="Enter your phone number"
                className="pl-10"
                error={validationErrors.contactPhone}
              />
            </div>
          </FormField>
        </div>
      </div>
    </div>
  );

  // Location and Date Step
  const LocationDateStep = ({ 
    requestData, 
    updateRequestData, 
    validationErrors,
    availableLocations = {},
    renderDateFields 
  }) => {
    // Get available locations with fallback to SA_LOCATIONS
    const LOCATIONS_DATA = useMemo(() => {
      if (!availableLocations || Object.keys(availableLocations).length === 0) {
        return SA_LOCATIONS;
      }
      return availableLocations;
    }, [availableLocations]);

    const handleProvinceChange = (province) => {
      updateRequestData('preferredProvince', province);
      updateRequestData('preferredCity', ''); // Clear city when province changes
      
      // Update location string
      const location = province;
      updateRequestData('preferredLocation', location);
    };

    const handleCityChange = (city) => {
      updateRequestData('preferredCity', city);
      
      // Update location string
      const province = requestData.preferredProvince;
      const location = city ? `${city}, ${province}` : province;
      updateRequestData('preferredLocation', location);
    };

    const getAvailableCities = (province) => {
      return LOCATIONS_DATA[province] || [];
    };

    return (
      <div className="space-y-6">
        {/* Location Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4">
            Preferred Location *
          </label>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Province Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">Province</label>
              <select
                value={requestData.preferredProvince || ''}
                onChange={(e) => handleProvinceChange(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                  validationErrors.location ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select Province</option>
                {Object.keys(LOCATIONS_DATA).map(province => (
                  <option key={province} value={province}>{province}</option>
                ))}
              </select>
            </div>

            {/* City Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-2">City/Town (Optional)</label>
              <select
                value={requestData.preferredCity || ''}
                onChange={(e) => handleCityChange(e.target.value)}
                disabled={!requestData.preferredProvince}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  validationErrors.location ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Any City/Town</option>
                {requestData.preferredProvince && getAvailableCities(requestData.preferredProvince).map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
          </div>

          {validationErrors.location && (
            <p className="text-red-500 text-sm mt-2">{validationErrors.location}</p>
          )}
          
          {requestData.preferredLocation && (
            <div className="mt-2 text-sm text-gray-600">
              Selected: <span className="font-medium">{requestData.preferredLocation}</span>
            </div>
          )}
        </div>

        {/* Flexible Location Toggle */}
        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <span className="font-medium text-gray-900">Flexible with location</span>
            <p className="text-sm text-gray-600">Open to suggestions in nearby areas</p>
          </div>
          <input
            type="checkbox"
            checked={requestData.isLocationFlexible || false}
            onChange={(e) => updateRequestData('isLocationFlexible', e.target.checked)}
            className="h-4 w-4 text-blue-600 rounded"
          />
        </label>

        {/* Date fields */}
        {renderDateFields()}
      </div>
    );
  };

  // Accommodation-specific preferences step
  const AccommodationPreferencesStep = ({ requestData, updateRequestData, typeConfig }) => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">Accommodation Preferences</h2>
        <p className="text-lg text-gray-600">Tell us about your ideal stay</p>
      </div>

      <div className="max-w-4xl mx-auto space-y-8">
        {/* Guest Details */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Users className="h-6 w-6 mr-3" />
            Guest Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Adults</label>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => updateRequestData('accommodation.adults', Math.max(1, requestData.accommodation.adults - 1))}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="px-6 py-3 bg-white border border-gray-300 rounded-md text-center min-w-[80px] font-medium">
                  {requestData.accommodation.adults}
                </span>
                <button
                  type="button"
                  onClick={() => updateRequestData('accommodation.adults', requestData.accommodation.adults + 1)}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Children</label>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => updateRequestData('accommodation.children', Math.max(0, requestData.accommodation.children - 1))}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="px-6 py-3 bg-white border border-gray-300 rounded-md text-center min-w-[80px] font-medium">
                  {requestData.accommodation.children}
                </span>
                <button
                  type="button"
                  onClick={() => updateRequestData('accommodation.children', requestData.accommodation.children + 1)}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Rooms</label>
              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={() => updateRequestData('accommodation.rooms', Math.max(1, requestData.accommodation.rooms - 1))}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="px-6 py-3 bg-white border border-gray-300 rounded-md text-center min-w-[80px] font-medium">
                  {requestData.accommodation.rooms}
                </span>
                <button
                  type="button"
                  onClick={() => updateRequestData('accommodation.rooms', requestData.accommodation.rooms + 1)}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Accommodation Type */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Accommodation Type</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {typeConfig.categories.map(category => (
              <button
                key={category.value}
                type="button"
                onClick={() => {
                  const current = requestData.accommodation.accommodationType || [];
                  const newTypes = current.includes(category.value)
                    ? current.filter(type => type !== category.value)
                    : [...current, category.value];
                  updateRequestData('accommodation.accommodationType', newTypes);
                }}
                className={`p-4 border rounded-lg text-center transition-colors ${
                  requestData.accommodation.accommodationType?.includes(category.value)
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Bed className="h-6 w-6 mx-auto mb-2" />
                <div className="text-sm font-medium">{category.label}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Budget */}
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <DollarSign className="h-6 w-6 mr-3" />
              Budget per Night
            </h3>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={requestData.accommodation.budget.flexible}
                onChange={(e) => updateRequestData('accommodation.budget.flexible', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-3 text-sm font-medium text-gray-700">Flexible</span>
            </label>
          </div>

          {!requestData.accommodation.budget.flexible ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Minimum (ZAR)</label>
                <input
                  type="number"
                  value={requestData.accommodation.budget.min}
                  onChange={(e) => updateRequestData('accommodation.budget.min', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Maximum (ZAR)</label>
                <input
                  type="number"
                  value={requestData.accommodation.budget.max}
                  onChange={(e) => updateRequestData('accommodation.budget.max', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="5000"
                />
              </div>
            </div>
          ) : (
            <p className="text-gray-600">
              Great! We'll present options across different price ranges and help you find the best value.
            </p>
          )}
        </div>

        {/* Amenities */}
        <div className="bg-gray-50 rounded-xl p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Preferred Amenities</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {typeConfig.amenities.map(amenity => (
              <button
                key={amenity.id}
                type="button"
                onClick={() => {
                  const current = requestData.accommodation.amenities || [];
                  const newAmenities = current.includes(amenity.id)
                    ? current.filter(a => a !== amenity.id)
                    : [...current, amenity.id];
                  updateRequestData('accommodation.amenities', newAmenities);
                }}
                className={`p-4 border rounded-lg text-center transition-colors ${
                  requestData.accommodation.amenities?.includes(amenity.id)
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <amenity.icon className="h-6 w-6 mx-auto mb-2" />
                <div className="text-xs font-medium">{amenity.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Booking Specifics Step (for non-accommodation)
  const BookingSpecificsStep = ({ requestData, updateRequestData, typeConfig, bookingType, renderAppointmentFields, renderRentalFields, renderVenueFields }) => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-3">{typeConfig.label} Details</h2>
        <p className="text-lg text-gray-600">Tell us about your specific requirements</p>
      </div>

      <div className="max-w-4xl mx-auto space-y-8">
        {/* Render booking-type specific fields */}
        {bookingType === 'appointment' && renderAppointmentFields()}
        {bookingType === 'rental' && renderRentalFields()}
        {bookingType === 'venue' && renderVenueFields()}

        {/* Universal Budget Section */}
        <div className="bg-gray-50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 flex items-center">
              <DollarSign className="h-6 w-6 mr-3" />
              Budget Range
            </h3>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={requestData.budget.flexible}
                onChange={(e) => updateRequestData('budget.flexible', e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-3 text-sm font-medium text-gray-700">Flexible</span>
            </label>
          </div>

          {!requestData.budget.flexible && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Minimum (ZAR)</label>
                <input
                  type="number"
                  value={requestData.budget.min}
                  onChange={(e) => updateRequestData('budget.min', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Maximum (ZAR)</label>
                <input
                  type="number"
                  value={requestData.budget.max}
                  onChange={(e) => updateRequestData('budget.max', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="10000"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Appointment-specific fields
  const renderAppointmentFields = ({ requestData, updateRequestData, typeConfig, submitStatus }) => (
    <div className="bg-gray-50 rounded-xl p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Service Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Service Type</label>
          <select
            value={requestData.appointment.serviceType}
            onChange={(e) => updateRequestData('appointment.serviceType', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Service Type</option>
            {typeConfig.services.map(service => (
              <option key={service.value} value={service.value}>{service.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Expected Duration (minutes)</label>
          <select
            value={requestData.appointment.duration}
            onChange={(e) => updateRequestData('appointment.duration', parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value={30}>30 minutes</option>
            <option value={60}>1 hour</option>
            <option value={90}>1.5 hours</option>
            <option value={120}>2 hours</option>
            <option value={180}>3 hours</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={requestData.appointment.isOnlineAvailable}
              onChange={(e) => updateRequestData('appointment.isOnlineAvailable', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-3 text-sm font-medium text-gray-700">I'm open to online/virtual consultations</span>
          </label>
        </div>
      </div>
    </div>
  );

  // Rental-specific fields
  const renderRentalFields = ({ requestData, updateRequestData, typeConfig, submitStatus }) => (
    <div className="bg-gray-50 rounded-xl p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Rental Details</h3>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
          <select
            value={requestData.rental.category}
            onChange={(e) => updateRequestData('rental.category', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Category</option>
            {typeConfig.categories.map(category => (
              <option key={category.value} value={category.value}>{category.label}</option>
            ))}
          </select>
        </div>
        <div className="space-y-4">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={requestData.rental.insurance}
              onChange={(e) => updateRequestData('rental.insurance', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-3 text-sm font-medium text-gray-700">Include insurance coverage</span>
          </label>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={requestData.rental.deliveryRequired}
              onChange={(e) => updateRequestData('rental.deliveryRequired', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-3 text-sm font-medium text-gray-700">Delivery/pickup service needed</span>
          </label>
        </div>
      </div>
    </div>
  );

  // Venue-specific fields  
  const renderVenueFields = ({ requestData, updateRequestData, typeConfig, submitStatus }) => (
    <div className="bg-gray-50 rounded-xl p-6">
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Event Details</h3>
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Venue Type</label>
          <select
            value={requestData.venue.eventType}
            onChange={(e) => updateRequestData('venue.eventType', e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Select Venue Type</option>
            {typeConfig.categories.map(category => (
              <option key={category.value} value={category.value}>{category.label}</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Expected Guests</label>
            <input
              type="number"
              value={requestData.venue.expectedGuests}
              onChange={(e) => updateRequestData('venue.expectedGuests', parseInt(e.target.value) || 0)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Number of guests"
            />
          </div>
        </div>
        <div>
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={requestData.venue.catering}
              onChange={(e) => updateRequestData('venue.catering', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-3 text-sm font-medium text-gray-700">Catering services required</span>
          </label>
        </div>
      </div>
    </div>
  );

  // Review and Submit Step
  const ReviewSubmitStep = ({ 
  requestData, 
  updateRequestData, 
  typeConfig, 
  submitStatus,
  isSubmitting,
  onSubmit 
}) => (
  <div className="space-y-8">
    <div className="text-center mb-8">
      <h2 className="text-3xl font-bold text-gray-900 mb-3">Review & Send Request</h2>
      <p className="text-lg text-gray-600">Please review your request before sending</p>
    </div>

    <div className="max-w-4xl mx-auto space-y-8">
      {/* Request Summary */}
      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-6">Request Summary</h3>
        <div className="space-y-4 text-sm">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600 font-medium">Contact Name:</span>
            <span className="font-semibold">{requestData.contactName}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600 font-medium">Email:</span>
            <span className="font-semibold">{requestData.contactEmail}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600 font-medium">Booking Type:</span>
            <span className="font-semibold">{typeConfig.label}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600 font-medium">Location:</span>
            <span className="font-semibold">
              {requestData.isLocationFlexible 
                ? 'Flexible' 
                : `${requestData.preferredCity || 'Any city'}, ${requestData.preferredProvince || 'Any province'}`
              }
            </span>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="text-center">
        <button
          onClick={onSubmit}
          disabled={isSubmitting}
          className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center mx-auto text-lg font-semibold"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
              Sending Request...
            </>
          ) : (
            <>
              <Send className="h-5 w-5 mr-3" />
              Send Request
            </>
          )}
        </button>

        {submitStatus === 'success' && (
          <div className="mt-4 text-green-600 font-medium">
            Request sent successfully! We'll get back to you soon.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="mt-4 text-red-600 font-medium">
            Failed to send request. Please try again.
          </div>
        )}
      </div>
    </div>
  </div>
);